import React, { useEffect, useMemo } from 'react';
import { Bus, useApplicationState } from '@apollo/core';
import { SportSelector } from '@apollo/core/src/state/sport/sport';
import { RacingSelector } from '@apollo/core/src/state/racing/racing';
import cx from 'classnames';
import { isEmpty } from 'lodash';
import { RaceMainTabs, sportService } from '../../core/constants';
import BespokeOutcome from './BespokeOutcome';
import EventStartTime from '../Sports/Tournament/Event/EventStartTime';
import { buildCdnUrl } from '../../core/utils';
import { EventLink } from '../../shared/components/Sport/SportLinks';
import RaceLink from '../../shared/components/Racing/RaceLink';
import UnauthenticatedButtons from '../Betslip/UnauthenticatedButtons';

const { PREMATCH, RACING } = sportService;

const BespokeMarket = ({ bespokeMarket }) => {
  const isSport = useMemo(
    () => bespokeMarket?.sportService !== RACING,
    [bespokeMarket?.sportService],
  );

  const { authenticated, layout } = useApplicationState();

  const event = isSport && bespokeMarket.correlatedEventIds?.length
    ? bespokeMarket.correlatedEventIds.reduce(
      (acc, id) => (isEmpty(acc) ? SportSelector.selectEventById(id) : acc),
      {},
    )
    : RacingSelector.selectRaceById(bespokeMarket?.eventId);

  const market = useMemo(
    () => ({
      ...bespokeMarket,
      event,
    }),
    [bespokeMarket, event],
  );

  useEffect(() => {
    if (!bespokeMarket.eventId || !isEmpty(event)) {
      return;
    }
    if (isSport) {
      Bus.emit(Bus.events.sport.sportLoadEvents, {
        filter: {
          fromDate: null,
          toDate: null,
          eventIds: bespokeMarket.correlatedEventIds,
          isOnlyActiveMarkets: true,
          isOnlyMainMarketTypes: true,
        },
        paging: {
          itemsPerPage: 10,
          currentPage: 0,
        },
      });
    } else {
      Bus.emit(Bus.events.racing.loadSingleRace, {
        raceId: bespokeMarket.eventId,
      });
    }
  }, [bespokeMarket.eventId, isSport, event]);

  const getEventName = (event) => {
    if (event.sportService === PREMATCH) {
      return (
        <EventLink className='event__link' event={event} urlParams={{ isBespokeMarket: true }}>
          {event.name}
        </EventLink>
      );
    }

    if (event.venueId) {
      return (
        <RaceLink
          key={event.raceId}
          className='event__link'
          raceDate={event.meeting.date}
          raceType={event.raceType.typeName}
          raceCountry={event.venue.country}
          raceVenue={event.venue.venueName}
          raceNumber={event.number}
          marketType={RaceMainTabs.BESPOKE_TAB}
        >
          {`${event.venue?.venueName}  R${event.number}`}
        </RaceLink>
      );
    }
    return '';
  };

  const bespokeMarketClassName = cx('bespoke-market');

  return (
    <div key={market.id} className={bespokeMarketClassName}>
      {market?.resources?.imageSrc && !layout.mobileDevice && (
        <div className='bespoke-market__image'>
          <img
            src={buildCdnUrl(market.resources.imageSrc, 'MEDIA_CDN_URL')}
            alt=''
            loading='lazy'
          />
        </div>
      )}
      {market?.resources?.mobileImageSrc && layout.mobileDevice && (
        <div className='bespoke-market__image'>
          <img
            src={buildCdnUrl(market.resources.mobileImageSrc, 'MEDIA_CDN_URL')}
            alt=''
            loading='lazy'
          />
        </div>
      )}
      <div className='bespoke-market__content'>
        <div className='bespoke-market__info'>
          <EventStartTime event={market.event || {}} withDate />
          <div className='bespoke-market__event'>
            {market.event ? <span>{getEventName(market.event)}</span> : null}
          </div>
        </div>
        <div className='bespoke-market__name'>
          <span title={market.localizedName}>{market.localizedName}</span>
        </div>
        <div className='bespoke-market__description'>
          <span title={market.localizedDescription}>{market.localizedDescription}</span>
        </div>
        <div className='bespoke-outcomes'>
          {market?.outcomes?.map((outcome) => (
            <BespokeOutcome
              key={outcome.id}
              market={market}
              outcome={outcome}
              disabled={!market.event?.status}
            />
          ))}
        </div>
        {!authenticated ? (
          <div className='bespoke-market__auth-buttons'>
            <UnauthenticatedButtons />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BespokeMarket;
